import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, useTheme } from '@mui/material';
import { tokens } from '../../../theme';
import { useAuth } from '../../../AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ModuleSidebar from './ModuleSidebar';
import LectureNotesTab from './LectureNotesTab';
import AssessmentsTab from './AssessmentsTab';
import StudentsTab from './StudentsTab';
import GradingsTab from './GradingsTab';
import NoteDialog from './NoteDialog';
import ViewNoteDialog from './ViewNoteDialog';
import AssessmentDialog from './AssessmentDialog';
import GradeDialog from './GradeDialog';

const ClassManagement = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { userID, getData, addData, updateData, deleteData, userRole } = useAuth();
  const navigate = useNavigate();

  // State management
  const [tabValue, setTabValue] = useState(0);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [notes, setNotes] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [assAttempt, setAssAttemp] = useState([]);
  const [students, setStudents] = useState([]);
  const [gradingsList, setGradingList] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openAssessmentDialog, setOpenAssessmentDialog] = useState(false);
  const [openGradeDialog, setOpenGradeDialog] = useState(false);
  const [openViewNoteDialog, setOpenViewNoteDialog] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isEditingAssessment, setIsEditingAssessment] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [newNote, setNewNote] = useState({ title: '', note_content: '', link: '', image: null });
  const [newAssessment, setNewAssessment] = useState({
    name: '',
    description: '',
    assess_number: '',
    weight: '',
    start_date: '',
    end_date: '',
    questions: [{ question: '', answers: [], correct_answer: '', question_type: 'structured' }], // Updated
    image: null,
  });
  const [studentGrade, setStudentGrade] = useState({ studentId: '', grade: '' });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const fileInputRef = useRef(null);

  // Toast configuration
  const toastOptions = {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  };

  // Fetch data on component mount
  useEffect(() => {
    if (userRole !== 'user') {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const response = await getData('proccess.php', 'gradings_V2');
        if (response.success) {
          setAssAttemp(response.data);
        }

        const gradingResponse = await getData('proccess.php', 'gradings_V2');
        if (gradingResponse.success) {
          setModules(gradingResponse.data);
          if (gradingResponse.data.length > 0) {
            setGradingList(gradingResponse.data);
          }
        }

        const modulesData = await getData('proccess.php', 'modules_V2');
        if (modulesData.success) {
          setModules(modulesData.data);
          if (modulesData.data.length > 0) {
            setSelectedModule(modulesData.data[0].id);
          }
        }

        const studentsData = await getData('proccess.php', 'students_V2');
        if (studentsData.success) {
          setStudents(studentsData.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('An unexpected error occurred while fetching data', toastOptions);
      }
    };

    fetchData();
  }, [userID, userRole, navigate, getData]);

  // Fetch module-specific data
  useEffect(() => {
    if (!selectedModule) return;

    const fetchModuleData = async () => {
      try {
        const notesData = await getData('proccess.php', 'lecture_note_V2');
        if (notesData.success) {
          const filteredNotes = notesData.data.filter((note) => note.module_ID === selectedModule);
          setNotes(filteredNotes);
        } else {
          toast.error(notesData.message || 'Failed to fetch notes', toastOptions);
        }

        const assessmentsData = await getData('proccess.php', 'assessments_V2');
        if (assessmentsData.success) {
          const filteredAssessments = assessmentsData.data
            .filter((assessment) => assessment.module_ID === selectedModule && assessment.id)
            .map((assessment) => ({
              ...assessment,
              id: assessment.id,
            }));
          setAssessments(filteredAssessments);
        } else {
          toast.error(assessmentsData.message || 'Failed to fetch assessments', toastOptions);
        }

        if (students.length > 0) {
          const enrolled = students.filter((student) => student.course === selectedModule && student.id);
          setEnrolledStudents(enrolled);
        } else {
          setEnrolledStudents([]);
        }
      } catch (error) {
        console.error('Error fetching module data:', error);
        toast.error('Error fetching module data', toastOptions);
      }
    };
    fetchModuleData();
  }, [selectedModule, students, getData]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSaveNote = async () => {
    try {
      const noteData = {
        ...newNote,
        module_ID: selectedModule,
      };

      if (isEditingNote) {
        const response = await updateData('proccess.php', 'lecture_note_V2', noteData, selectedNote.id);
        if (response.success) {
          setNotes(notes.map((note) => (note.id === selectedNote.id ? { ...noteData, id: selectedNote.id } : note)));
          toast.success('Note updated successfully', toastOptions);
        } else {
          toast.error(response.message || 'Failed to update note', toastOptions);
        }
      } else {
        const response = await addData('proccess.php', 'lecture_note_V2', noteData);
        if (response.success) {
          setNotes([...notes, { ...noteData, id: response.id }]);
          toast.success('Note added successfully', toastOptions);
        } else {
          toast.error(response.message || 'Failed to add note', toastOptions);
        }
      }

      setOpenNoteDialog(false);
      setNewNote({ title: '', note_content: '', link: '', image: null });
      setIsEditingNote(false);
      setSelectedNote(null);
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Error saving note', toastOptions);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await deleteData('proccess.php', 'lecture_note_V2', noteId);
      if (response.success) {
        setNotes(notes.filter((note) => note.id !== noteId));
        toast.success('Note deleted successfully', toastOptions);
      } else {
        toast.error(response.message || 'Failed to delete note', toastOptions);
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Error deleting note', toastOptions);
    }
  };

  const handleSaveAssessment = async () => {
    try {
      const assessmentData = {
        ...newAssessment,
        module_ID: selectedModule,
        questions: JSON.stringify(
          newAssessment.questions.map((q) => ({
            question: q.question || '',
            answers: q.answers,
            correct_answer: q.correct_answer || '', // Updated
            question_type: q.question_type,
          }))
        ),
      };

      if (isEditingAssessment) {
        const response = await updateData('proccess.php', 'assessments_V2', assessmentData, selectedAssessment.id);
        if (response.success) {
          setAssessments(assessments.map((assessment) =>
            assessment.id === selectedAssessment.id ? { ...assessmentData, id: selectedAssessment.id } : assessment
          ));
          toast.success('Assessment updated successfully', toastOptions);
        } else {
          toast.error(response.message || 'Failed to update assessment', toastOptions);
        }
      } else {
        const response = await addData('proccess.php', 'assessments_V2', assessmentData);
        if (response.success) {
          setAssessments([...assessments, { ...assessmentData, id: response.id }]);
          toast.success('Assessment added successfully', toastOptions);
        } else {
          toast.error(response.message || 'Failed to add assessment', toastOptions);
        }
      }

      setOpenAssessmentDialog(false);
      setNewAssessment({
        name: '',
        description: '',
        assess_number: '',
        weight: '',
        start_date: '',
        end_date: '',
        questions: [{ question: '', answers: [], correct_answer: '', question_type: 'structured' }], 
        image: null,
      });
      setIsEditingAssessment(false);
      setSelectedAssessment(null);
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Error saving assessment', toastOptions);
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    try {
      const response = await deleteData('proccess.php', 'assessments_V2', assessmentId);
      if (response.success) {
        setAssessments(assessments.filter((assessment) => assessment.id !== assessmentId));
        toast.success('Assessment deleted successfully', toastOptions);
      } else {
        toast.error(response.message || 'Failed to delete assessment', toastOptions);
      }
    } catch (error) {
      console.error('Error deleting assessment:', error);
      toast.error('Error deleting assessment', toastOptions);
    }
  };

  const handleGradeSubmission = async () => {
    try {
      const response = await addData('proccess.php', 'gradings_V2', {
        student_id: studentGrade.studentId,
        assessment_id: selectedAssessment.id,
        grade: studentGrade.grade,
        module_id: selectedModule,
      });
      if (response.success) {
        setOpenGradeDialog(false);
        setStudentGrade({ studentId: '', grade: '' });
        toast.success('Grade submitted successfully', toastOptions);
      } else {
        toast.error(response.message || 'Failed to submit grade', toastOptions);
      }
    } catch (error) {
      console.error('Error submitting grade:', error);
      toast.error('Error submitting grade', toastOptions);
    }
  };

  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (type === 'note') {
      setNewNote({ ...newNote, image: file });
      toast.info('File selected for note', toastOptions);
    } else {
      setNewAssessment({ ...newAssessment, image: file });
      toast.info('File selected for assessment', toastOptions);
    }
  };

  return (
    <Box display="flex" minHeight="100vh">
      <ToastContainer />
      <ModuleSidebar
        modules={modules}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        colors={colors}
      />
      <Box flexGrow={1} p={3}>
        <Typography variant="h2" mb={3}>
          {modules.find((m) => m.id === selectedModule)?.module_name || 'Select a Module'}
        </Typography>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Lecture Notes" />
          <Tab label="Assessments" />
          <Tab label="Students" />
          <Tab label="Gradings" />
        </Tabs>
        {tabValue === 0 && (
          <LectureNotesTab
            notes={notes}
            setOpenNoteDialog={setOpenNoteDialog}
            setIsEditingNote={setIsEditingNote}
            setNewNote={setNewNote}
            setSelectedNote={setSelectedNote}
            handleDeleteNote={handleDeleteNote}
            setOpenViewNoteDialog={setOpenViewNoteDialog}
          />
        )}
        {tabValue === 1 && (
          <AssessmentsTab
            assessments={assessments}
            setOpenAssessmentDialog={setOpenAssessmentDialog}
            setIsEditingAssessment={setIsEditingAssessment}
            setNewAssessment={setNewAssessment}
            setSelectedAssessment={setSelectedAssessment}
            setOpenGradeDialog={setOpenGradeDialog}
            handleDeleteAssessment={handleDeleteAssessment}
          />
        )}
        {tabValue === 2 && <StudentsTab enrolledStudents={enrolledStudents} />}
        {tabValue === 3 && (
          <GradingsTab
            assessments={assessments}
            enrolledStudents={enrolledStudents}
            gradingsList={gradingsList}
            setSelectedAssessment={setSelectedAssessment}
            setSelectedStudent={setSelectedStudent}
            selectedAssessment={selectedAssessment}
            selectedStudent={selectedStudent}
            assAttempts={assAttempt}
          />
        )}
        <NoteDialog
          open={openNoteDialog}
          isEditing={isEditingNote}
          newNote={newNote}
          setNewNote={setNewNote}
          selectedNote={selectedNote}
          setIsEditingNote={setIsEditingNote}
          setSelectedNote={setSelectedNote}
          handleSaveNote={handleSaveNote}
          handleFileUpload={handleFileUpload}
          fileInputRef={fileInputRef}
          setOpenNoteDialog={setOpenNoteDialog}
        />
        <ViewNoteDialog
          open={openViewNoteDialog}
          selectedNote={selectedNote}
          setSelectedNote={setSelectedNote}
          setOpenViewNoteDialog={setOpenViewNoteDialog}
          colors={colors}
        />
        <AssessmentDialog
          open={openAssessmentDialog}
          isEditing={isEditingAssessment}
          newAssessment={newAssessment}
          setNewAssessment={setNewAssessment}
          selectedAssessment={selectedAssessment}
          setIsEditingAssessment={setIsEditingAssessment}
          setSelectedAssessment={setSelectedAssessment}
          handleSaveAssessment={handleSaveAssessment}
          handleFileUpload={handleFileUpload}
          fileInputRef={fileInputRef}
          setOpenAssessmentDialog={setOpenAssessmentDialog}
          colors={colors}
        />
        <GradeDialog
          open={openGradeDialog}
          studentGrade={studentGrade}
          setStudentGrade={setStudentGrade}
          enrolledStudents={enrolledStudents}
          handleGradeSubmission={handleGradeSubmission}
          setOpenGradeDialog={setOpenGradeDialog}
        />
      </Box>
    </Box>
  );
};

export default ClassManagement;